import { Dispatch, SetStateAction, useMemo } from "react"
import { useSubscription } from "react-relay"
import { graphql, GraphQLSubscriptionConfig } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { PatientFileLockChange } from "@/modules/common/dialog/request-take-over-dialog"
import { usePatientFileLockChangeSubscription as usePatientFileLockChangeSubscriptionType } from "@/modules/patients/services/__generated__/usePatientFileLockChangeSubscription.graphql"

const patientFileLockChangeSubscription = graphql`
  subscription usePatientFileLockChangeSubscription($patientId: ID!) {
    patientFileLockChanged(patientId: $patientId) {
      lock {
        ...requestTakeOverDialog_patientFileLockFragment
        lockedBy
        appointment {
          chatDetails {
            chatThreadId
          }
        }
      }
      action
      takeOverRequestCountdownValue
    }
  }
`

type PatientFileLockTakeOverStatus = "GRANTED" | "DENIED" | "ABANDONED"

type PatientFileLockTakeOverResult = {
  status: PatientFileLockTakeOverStatus
  chatThreadId: string | null
}

const usePatientFileLockChangeSubscription = (
  patientId: string,
  setPatientFileLockChange: Dispatch<SetStateAction<PatientFileLockChange | undefined>>,
  onPatientFileLockTakeOverEnded?: (result: PatientFileLockTakeOverResult) => void,
) => {
  const { userName } = useUser()

  const subscriptionConfig: GraphQLSubscriptionConfig<usePatientFileLockChangeSubscriptionType> = useMemo(() => {
    return {
      subscription: patientFileLockChangeSubscription,
      variables: { patientId },
      onNext: (res) => {
        if (!res) {
          return
        }

        const {
          patientFileLockChanged: { lock, action, takeOverRequestCountdownValue },
        } = res

        setPatientFileLockChange({
          action,
          takeOverRequestCountdownValue,
        })

        if ((action === "TAKEN" || action === "ALLOWED") && lock.lockedBy === userName) {
          onPatientFileLockTakeOverEnded?.({
            status: "GRANTED",
            chatThreadId: lock.appointment?.chatDetails?.chatThreadId ?? null,
          })
        }

        if (action === "DENIED" || action === "CANCEL_REQUEST") {
          onPatientFileLockTakeOverEnded?.({ status: "DENIED", chatThreadId: null })
        }
      },
    }
  }, [onPatientFileLockTakeOverEnded, setPatientFileLockChange, patientId, userName])

  return useSubscription<usePatientFileLockChangeSubscriptionType>(subscriptionConfig)
}

export { type PatientFileLockTakeOverResult, type PatientFileLockTakeOverStatus, usePatientFileLockChangeSubscription }
